import React, { useState, useEffect } from 'react';
// import { useAuth } from '../../contexts/AuthContext'; // Removed unused import
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  Users, 
  Search, 
  ChevronDown, 
  ChevronUp,
  Award,
  Clock,
  CheckCircle,
  XCircle,
  Mail,
  Phone,
  BookOpen,
  GraduationCap,
  Eye,
  Edit,
  UserCheck
} from 'lucide-react';

const AdminStudents = () => {
  // const { user } = useAuth(); // Removed unused variable
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [branchFilter, setBranchFilter] = useState('all');
  const [sortBy, setSortBy] = useState('rank');
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showAllocationModal, setShowAllocationModal] = useState(false);

  const branches = [
    'Computer Science',
    'Mechanical Engineering',
    'Electrical Engineering',
    'Civil Engineering',
    'Chemical Engineering'
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'allocated', label: 'Allocated' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'completed', label: 'Completed' },
    { value: 'rejected', label: 'Rejected' }
  ];

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      const response = await axios.get('/api/admin/students');
      setStudents(response.data);
    } catch (error) {
      console.error('Error fetching students:', error);
      toast.error('Failed to fetch students');
    } finally {
      setLoading(false);
    }
  };

  const handleAllocateBranches = async () => {
    try {
      setLoading(true);
      await axios.post('/api/admin/allocate-branches');
      toast.success('Branch allocation completed successfully!');
      await fetchStudents();
    } catch (error) {
      console.error('Error allocating branches:', error);
      toast.error('Failed to allocate branches');
    } finally {
      setLoading(false);
    }
  };

  const handleManualAllocation = async (studentId, branch) => {
    try {
      await axios.post('/api/admin/manual-allocation', {
        studentId,
        branch
      });
      toast.success('Branch allocated successfully!');
      setShowAllocationModal(false);
      await fetchStudents();
    } catch (error) {
      console.error('Error in manual allocation:', error);
      toast.error('Failed to allocate branch');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      allocated: { color: 'bg-blue-100 text-blue-800', icon: Award },
      accepted: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      completed: { color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
      rejected: { color: 'bg-red-100 text-red-800', icon: XCircle }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const filteredStudents = students
    .filter(student => {
      // Add null checks for userId
      const userName = student.userId?.name || '';
      const userEmail = student.userId?.email || '';
      
      const matchesSearch = userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           userEmail.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || student.status === statusFilter;
      const matchesBranch = branchFilter === 'all' || student.allocatedBranch === branchFilter;
      return matchesSearch && matchesStatus && matchesBranch;
    })
    .sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'rank':
          aValue = a.rank || 999999;
          bValue = b.rank || 999999;
          break;
        case 'name':
          aValue = a.userId?.name || '';
          bValue = b.userId?.name || '';
          break;
        case 'marks':
          aValue = a.twelfthMarks?.total || 0;
          bValue = b.twelfthMarks?.total || 0;
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        default:
          return 0;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Student Management</h1>
            <p className="text-gray-600 mt-1">
              Manage student applications, rankings, and branch allocations
            </p>
          </div>
          <button
            onClick={handleAllocateBranches}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center"
          >
            <UserCheck className="h-4 w-4 mr-2" />
            Auto Allocate Branches
          </button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search students..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            {statusOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {/* Branch Filter */}
          <select
            value={branchFilter}
            onChange={(e) => setBranchFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="all">All Branches</option>
            {branches.map(branch => (
              <option key={branch} value={branch}>
                {branch}
              </option>
            ))}
          </select>

          {/* Sort Options */}
          <div className="flex space-x-2">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="rank">Sort by Rank</option>
              <option value="name">Sort by Name</option>
              <option value="marks">Sort by Marks</option>
              <option value="status">Sort by Status</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>

      {/* Students Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rank
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Student Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Academic Performance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Branch Preferences
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredStudents.map((student) => (
                <tr key={student._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="bg-primary-100 text-primary-800 px-2 py-1 rounded-full text-sm font-semibold">
                        #{student.rank || 'N/A'}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <span className="text-primary-600 font-medium text-sm">
                            {student.userId?.name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {student.userId?.name || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center">
                          <Mail className="h-3 w-3 mr-1" />
                          {student.userId?.email || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center">
                          <Phone className="h-3 w-3 mr-1" />
                          {student.userId?.phone || 'N/A'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      <div className="flex items-center mb-1">
                        <GraduationCap className="h-4 w-4 mr-1 text-gray-400" />
                        <span className="font-medium">12th: {student.twelfthMarks?.total || 0}/300</span>
                      </div>
                      <div className="text-xs text-gray-500">
                        ({student.twelfthMarks?.percentage?.toFixed(2) || '0.00'}%)
                      </div>
                      <div className="flex items-center mt-1">
                        <BookOpen className="h-4 w-4 mr-1 text-gray-400" />
                        <span className="text-xs">10th: {student.highSchoolMarks?.total || 0}/400</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {student.branchChoices.map((choice, index) => (
                        <div key={index} className="mb-1">
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-800">
                            {choice.priority}. {choice.branch}
                          </span>
                        </div>
                      ))}
                      {student.allocatedBranch && (
                        <div className="mt-2">
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-green-100 text-green-800 font-medium">
                            Allocated: {student.allocatedBranch}
                          </span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(student.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          setSelectedStudent(student);
                          setShowDetailsModal(true);
                        }}
                        className="text-primary-600 hover:text-primary-900 p-1 rounded"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      {student.status === 'pending' && (
                        <button
                          onClick={() => {
                            setSelectedStudent(student);
                            setShowAllocationModal(true);
                          }}
                          className="text-green-600 hover:text-green-900 p-1 rounded"
                          title="Allocate Branch"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredStudents.length === 0 && (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No students found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting your search criteria or filters.
            </p>
          </div>
        )}
      </div>

      {/* Student Details Modal */}
      {showDetailsModal && selectedStudent && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Student Details: {selectedStudent.userId?.name || 'N/A'}
              </h3>
              <div className="space-y-3 text-sm">
                <div>
                  <span className="font-medium">Email:</span> {selectedStudent.userId?.email || 'N/A'}
                </div>
                <div>
                  <span className="font-medium">Phone:</span> {selectedStudent.userId?.phone || 'N/A'}
                </div>
                <div>
                  <span className="font-medium">Rank:</span> #{selectedStudent.rank}
                </div>
                <div>
                  <span className="font-medium">Status:</span> {getStatusBadge(selectedStudent.status)}
                </div>
                <div>
                  <span className="font-medium">12th Marks:</span> {selectedStudent.twelfthMarks?.total || 0}/300 ({selectedStudent.twelfthMarks?.percentage?.toFixed(2) || '0.00'}%)
                </div>
                <div>
                  <span className="font-medium">10th Marks:</span> {selectedStudent.highSchoolMarks?.total || 0}/400 ({selectedStudent.highSchoolMarks?.percentage?.toFixed(2) || '0.00'}%)
                </div>
                {selectedStudent.allocatedBranch && (
                  <div>
                    <span className="font-medium">Allocated Branch:</span> {selectedStudent.allocatedBranch}
                  </div>
                )}
              </div>
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manual Allocation Modal */}
      {showAllocationModal && selectedStudent && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Allocate Branch: {selectedStudent.userId?.name || 'N/A'}
              </h3>
              <div className="space-y-3">
                {branches.map(branch => (
                  <button
                    key={branch}
                    onClick={() => handleManualAllocation(selectedStudent._id, branch)}
                    className="w-full text-left p-3 border border-gray-300 rounded active:bg-blue-500 hover:bg-gray-50 transition-colors"
                  >
                    {branch}
                  </button>
                ))}
              </div>
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowAllocationModal(false)}
                  className="px-4 py-2 bg-gray-300 active:bg-red-500 text-gray-700 rounded hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminStudents;